import {
  ProgressBar as ReactAriaProgressBar,
  Label as ReactAriaLabel,
} from "react-aria-components";

import { useUIVersion } from "#hooks";
import { customTv, cn } from "#utils";

const containerStyles = customTv({
  base: "h-4 w-full overflow-hidden rounded-lg border border-outline-brand",
  variants: {
    uiVersion: {
      v1: "border-none rounded-none bg-legacy-orange-L60 h-1",
      v2: "",
    },
    variant: {
      rounded: "",
      square: "rounded-none",
    },
  },
  defaultVariants: {
    uiVersion: "v2",
    variant: "rounded",
  },
});

const barStyles = customTv({
  base: "h-full rounded-lg bg-foreground-brand transition-all duration-300 ease-out",
  variants: {
    uiVersion: {
      v1: "rounded-none bg-legacy-orange-base",
      v2: "",
    },
    variant: {
      rounded: "",
      square: "rounded-none",
    },
  },
  defaultVariants: {
    uiVersion: "v2",
    variant: "rounded",
  },
});

const labelStyles = customTv({
  base: "text-sm text-gray-700 font-medium",
  variants: {
    uiVersion: {
      v1: "text-label-extra-small",
      v2: "",
    },
  },
  defaultVariants: {
    uiVersion: "v2",
  },
});

export interface ProgressBarProps {
  /** Visual label shown above the bar. Required when `indeterminate` is false. */
  label?: string;
  /** Required when `indeterminate` is false or omitted */
  percentage?: number;
  /** When true, renders a shimmer animation indicating unknown progress */
  indeterminate?: boolean;
  /** Controls corner rounding. Defaults to "rounded". */
  variant?: "rounded" | "square";
}

export function ProgressBar({
  label,
  percentage = 0,
  indeterminate = false,
  variant = "rounded",
}: ProgressBarProps) {
  const uiVersion = useUIVersion();

  return (
    <ReactAriaProgressBar
      aria-label={!label || indeterminate ? (label ?? "Loading") : undefined}
      className="w-full"
      isIndeterminate={indeterminate}
      value={indeterminate ? undefined : percentage}
    >
      {({ percentage: pct, valueText }) => (
        <>
          {!indeterminate ? (
            <div
              className={cn("flex items-center justify-between", uiVersion === "v2" ? "mb-2" : "")}
            >
              <ReactAriaLabel className={cn(labelStyles({ uiVersion }))}>{label}</ReactAriaLabel>
              {uiVersion === "v2" ? (
                <span className="text-sm text-gray-700 ml-2 font-medium">{valueText}</span>
              ) : null}
            </div>
          ) : null}
          <div className={cn(containerStyles({ uiVersion, variant }))}>
            {indeterminate ? (
              <div className="relative h-full w-full overflow-hidden">
                <div
                  className={cn(
                    "absolute inset-y-0 left-0 w-1/2 animate-loading-overlay-slide",
                    uiVersion === "v1"
                      ? "bg-legacy-orange-base"
                      : cn("bg-foreground-brand", variant === "rounded" ? "rounded-lg" : undefined),
                  )}
                />
              </div>
            ) : (
              <div className={cn(barStyles({ uiVersion, variant }))} style={{ width: `${pct}%` }} />
            )}
          </div>
        </>
      )}
    </ReactAriaProgressBar>
  );
}
